import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Check, Linkedin, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/lib/site";
import { CATEGORIES, modulesIn, moduleHref } from "@/lib/modules";
import { subscribeNewsletter } from "@/lib/api";
import { Logo, Container } from "@/components/ui-kit";

const COMPANY = [
  { label: "Solutions", to: "/solutions" }, { label: "For contractors", to: "/for-contractors" }, { label: "H&S library preview", to: "/library" }, { label: "Pricing", to: "/pricing" }, { label: "Compare", to: "/compare" }, { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" }, { label: "Help centre", to: "/help" }, { label: "Contact", to: "/contact" },
];
const LEGAL = [{ label: "Privacy", to: "/privacy" }, { label: "Terms", to: "/terms" }, { label: "Security", to: "/security" }];

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      await subscribeNewsletter(email);
      setState("done");
      toast.success("You're on the dispatch list.");
      setEmail("");
    } catch {
      setState("idle");
      toast.error("Could not subscribe — try again.");
    }
  };
  return (
    <form onSubmit={submit} className="mt-5 flex gap-2" data-testid="newsletter-form">
      <input
        type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="work@company.com"
        data-testid="newsletter-email-input"
        className="flex-1 min-w-0 rounded-full border border-mk-border bg-mk-bg px-4 py-2.5 text-sm text-mk-text placeholder:text-mk-muted focus:outline-none focus:border-mk-lime/60 focus:ring-2 focus:ring-mk-lime/20"
      />
      <button type="submit" disabled={state === "loading"} data-testid="newsletter-submit-btn" className="btn-lime !px-4 !py-2.5">
        {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : state === "done" ? <Check className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
      </button>
    </form>
  );
};

const Footer = () => (
  <footer className="relative border-t border-mk-border bg-mk-bg" data-testid="footer">
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-mk-lime/50 to-transparent" />
    <Container className="py-16 lg:py-20">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Logo withTagline size="h-10" />
          <p className="mt-5 text-sm text-mk-dim leading-relaxed max-w-sm">{SITE.description}</p>
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-mk-text">The Fixlr dispatch</div>
            <p className="mt-1 text-xs text-mk-muted">Product releases, ops playbooks, customer stories. Monthly.</p>
            <Newsletter />
          </div>
          <div className="mt-6 flex items-center gap-3">
            {[["LinkedIn", Linkedin], ["Twitter", Twitter], ["YouTube", Youtube]].map(([l, I]) => (
              <a key={l} href="#" aria-label={l} data-testid={`footer-social-${l.toLowerCase()}`} className="grid place-items-center h-9 w-9 rounded-full border border-mk-border text-mk-dim hover:text-mk-lime hover:border-mk-lime/50 transition-colors"><I className="h-4 w-4" /></a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-8">
          {CATEGORIES.map((c) => (
            <div key={c.slug}>
              <Link to={`/platform/${c.slug}`} data-testid={`footer-cat-${c.slug}`} className="text-xs font-semibold uppercase tracking-wider text-mk-text hover:text-mk-lime transition-colors">{c.name}</Link>
              <ul className="mt-3 space-y-2">
                {modulesIn(c.slug).map((m) => (
                  <li key={m.slug}><Link to={moduleHref(m)} className="text-[13px] text-mk-dim hover:text-mk-text transition-colors">{m.name}</Link></li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-mk-text">Company</div>
            <ul className="mt-3 space-y-2">
              {COMPANY.map((l) => <li key={l.to}><Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase()}`} className="text-[13px] text-mk-dim hover:text-mk-text transition-colors">{l.label}</Link></li>)}
              <li><a href={SITE.loginUrl} target="_blank" rel="noreferrer" data-testid="footer-signin-link" className="text-[13px] text-mk-dim hover:text-mk-text transition-colors">Sign in</a></li>
            </ul>
            <div className="mt-6 text-xs font-semibold uppercase tracking-wider text-mk-text">Legal</div>
            <ul className="mt-3 space-y-2">
              {LEGAL.map((l) => <li key={l.to}><Link to={l.to} data-testid={`footer-legal-${l.label.toLowerCase()}`} className="text-[13px] text-mk-dim hover:text-mk-text transition-colors">{l.label}</Link></li>)}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-mk-border bg-mk-surface p-5">
            <div className="flex items-center gap-2 text-xs text-mk-dim"><span className="live-dot" />All systems operational</div>
            <div className="mt-3 font-mono text-[11px] text-mk-muted space-y-1">
              <div>app.fixlr.app · 99.98%</div>
              <div>supplier.fixlr.app · 99.99%</div>
              <div>api.fixlr.app · 99.97%</div>
            </div>
            <a href={SITE.signupUrl} target="_blank" rel="noreferrer" data-testid="footer-start-trial-btn" className="mt-4 btn-lime w-full !py-2 text-xs">Start free trial</a>
          </div>
        </div>
      </div>

      <div className="mt-14 pt-6 border-t border-mk-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-mk-muted">
        <div>© {new Date().getFullYear()} Fixlr · Fix more, Manage less</div>
        <div className="font-mono">Multi-tenant · White-label · Production-grade</div>
        <a href={`mailto:${SITE.email}`} className="hover:text-mk-lime transition-colors" data-testid="footer-email">{SITE.email}</a>
      </div>
    </Container>
  </footer>
);

export default Footer;
